import { Box, LinearProgress } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import SimpleAlert from "../../../components/display/SimpleAlert";
import NotFoundFullScreenMessage from "../../../components/fullScreenMessage/NotFoundFullScreenMessage";
import { PETS_NOTES, PETS_GENERAL } from "../../../enums/QueryKeys";
import { GENERAL, NOTES } from "../enums/TabsRequestParams";
import AddPetNoteForm from "../form/AddPetNoteForm";
import { deletePetNote } from "../../../enums/queryFunction/Delete";
import { useCanUserAddOrRemoveNotes } from "../hooks/useCanUserAddOrRemoveNotes";
import { useFetch } from "../../../hooks/Fetching";
import { PET_DETAILS_ } from "../../../enums/UrlPaths";
import PetNoteListItem from "../components/PetNoteListItem";

export default function PetNotes() {
  const { id: petId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const { data, isLoading, error } = useFetch(
    PET_DETAILS_ + petId + "?tab=" + NOTES,
    null,
    PETS_NOTES + petId
  );

  const {
    data: petData,
    isLoading: isPetLoading,
    error: petError,
  } = useFetch(
    PET_DETAILS_ + petId + "?tab=" + GENERAL,
    null,
    [PETS_GENERAL + petId]
  );

  const queryClient = useQueryClient();
  const canUserAddOrRemoveNotes = useCanUserAddOrRemoveNotes(
    petData,
    isPetLoading
  );

  const handleDelete = async (data) => {
    await deletePetNote(data);
    queryClient.invalidateQueries({ queryKey: [PETS_NOTES + petId] });
  };

  if (isLoading || isPetLoading) return <LinearProgress />;

  if (error || petError) {
    const errorObj = error || petError;
    if (
      errorObj?.response?.status === 403 ||
      String(errorObj?.response?.data).toUpperCase() === String("FORBIDDEN")
    ) {
      navigate(-1);
      return;
    }
    return <NotFoundFullScreenMessage testId={"pet.notes.error-alert"}/>;
  }

  if (!data || data.length === 0)
    return (
      <>
        {canUserAddOrRemoveNotes && <AddPetNoteForm />}
        <SimpleAlert message={t("NoNotesCurrentlyAvailable")} severity="info" testId={"pet.notes.empty-alert"}/>
      </>
    );
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      sx={{ minWidth: 0 }}
    >
      {canUserAddOrRemoveNotes && <AddPetNoteForm />}
      {data.map((note) => (
        <PetNoteListItem
          key={note?.id}
          note={note}
          canUserAddOrRemoveNotes={canUserAddOrRemoveNotes}
          handleDelete={handleDelete}
        />
      ))}
    </Box>
  );
}
